import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations.
   * Complementary recommendations fall back to related recommendations.
   */
  async #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    try {
      let resolvedIntent = intent || 'related';
      let result = await this.#fetchCachedRecommendations(productId, sectionId, resolvedIntent);

      const needsRelatedFallback =
        resolvedIntent === 'complementary' && (!result.success || !this.#responseHasRecommendations(result.data));

      if (needsRelatedFallback) {
        resolvedIntent = 'related';
        result = await this.#fetchCachedRecommendations(productId, sectionId, resolvedIntent);
      }

      if (!result.success) {
        if (!Shopify.designMode) {
          this.#handleError(new Error(`Server returned ${result.status}`));
        }
        return;
      }

      if (!this.#responseHasRecommendations(result.data)) {
        this.#handleError(new Error('No recommendations available'));
        return;
      }

      this.dataset.recommendationsPerformed = 'true';
      this.dataset.resolvedIntent = resolvedIntent;
      morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#handleError(error);
    }
  }

  /**
   * Checks only this block inside the returned section HTML.
   * @param {string | undefined} html
   * @returns {boolean}
   */
  #responseHasRecommendations(html) {
    if (!html?.trim()) return false;

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const blockId = globalThis.CSS?.escape ? CSS.escape(this.id) : this.id;
    const returnedBlock = documentFragment.querySelector(`#${blockId}`);

    return returnedBlock?.querySelector('[data-has-recommendations="true"]') !== null;
  }

  /**
   * Fetches the recommendations and caches the result for future use.
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number, data?: undefined }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way.
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}